/**
 * OffspringPanel — a piece's offspring (FEAT-068).
 *
 * Extracted from AmplificationsPanel.tsx (FEAT-092) — Offspring only ever
 * shared a file with amplifications because they shared a side-column
 * section, not because they're related. Creates a real, independent piece
 * (POST /pieces/:id/offspring, S-329), then immediately triggers its
 * pipeline run (the existing POST /pieces/:newId/run — this form never
 * re-implements any run gating). The publication list is pre-filtered to
 * linkedin_newsletter-eligible ones (S-328); the server re-validates
 * regardless.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRole } from "@/lib/useRole";
import { useApi, useApiQuery } from "@/lib/api";
import { uiStateLabel } from "@/lib/ui-state";
import { Icon } from "../Icon";
import type { PieceOffspring } from "@/types/piece";

export interface OffspringPanelProps {
  pieceId: number;
}

export function OffspringPanel({ pieceId }: OffspringPanelProps) {
  // FEAT-108 / S-438 — creating a piece is Admin and Editor work.
  const { canEdit } = useRole();
  const { data: offspringData, isLoading: offspringLoading } = useApiQuery<{ data: PieceOffspring[] }>(
    ["piece-offspring", pieceId],
    `/pieces?parent_piece_id=${pieceId}`
  );
  const offspring = offspringData?.data ?? [];
  const [offspringFormOpen, setOffspringFormOpen] = useState(false);

  return (
    <div className="panel-body">
      {offspringLoading && <div className="panel-empty">Loading…</div>}
      {!offspringLoading && offspring.length === 0 && (
        <div className="panel-empty">No offspring pieces yet.</div>
      )}
      {offspring.length > 0 && (
        <ul className="report-list">
          {offspring.map((child) => (
            <li key={child.id}>
              {/* .report-row is a 32px-icon + 1fr-text CSS grid (app-overrides.css)
                  — the trail rows render the icon span;
                  omitting it left the title squeezed into the icon column. */}
              <Link to={`/piece/${child.id}`} className="report-row">
                <span className="report-row-icon">
                  <Icon name="doc" size={18} />
                </span>
                <span className="report-row-body">
                  <span className="report-row-title">{child.title}</span>
                  <span className="report-row-sub">{uiStateLabel(child.ui_state)}</span>
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}

      {canEdit && (
        <div style={{ marginTop: 16 }}>
          <button
            className="btn-dec btn-review"
            onClick={() => setOffspringFormOpen((v) => !v)}
          >
            {offspringFormOpen ? "Cancel" : "Create Offspring"}
          </button>
        </div>
      )}
      {canEdit && offspringFormOpen && (
        <OffspringPieceForm pieceId={pieceId} onDone={() => setOffspringFormOpen(false)} />
      )}
    </div>
  );
}

function OffspringPieceForm({ pieceId, onDone }: { pieceId: number; onDone: () => void }) {
  const api = useApi();
  const qc = useQueryClient();
  const { data, isLoading } = useApiQuery<{ data: Array<{ id: number; name: string }> }>(
    ["offspring-eligible-publications"],
    "/publications?publish_target_type=linkedin_newsletter"
  );
  const eligiblePublications = data?.data ?? [];
  const [publicationId, setPublicationId] = useState<number | "">("");
  const [contentFormat, setContentFormat] = useState<"short" | "regular" | "long">("regular");
  const [scope, setScope] = useState("");
  const [createdPiece, setCreatedPiece] = useState<{ id: number; title: string } | null>(null);

  const create = useMutation({
    mutationFn: async () => {
      if (!publicationId) throw new Error("Pick a target publication.");
      const created = (await api(`/pieces/${pieceId}/offspring`, {
        method: "POST",
        body: JSON.stringify({
          publication_id: publicationId,
          content_format: contentFormat,
          scope: scope.trim() || undefined,
        }),
      })) as { id: number; title: string };
      // Reuses the existing run-trigger endpoint as-is — no new gating logic.
      await api(`/pieces/${created.id}/run`, { method: "POST" });
      return created;
    },
    onSuccess: (created) => {
      setCreatedPiece(created);
      void qc.invalidateQueries({ queryKey: ["piece-offspring", pieceId] });
    },
  });

  if (createdPiece) {
    return (
      <div className="settings-form" style={{ marginTop: 8, paddingTop: 8, borderTop: "1px solid var(--rule)" }}>
        <p className="panel-empty" style={{ margin: "0 0 8px", textAlign: "left" }}>
          Offspring created and running.
        </p>
        <Link to={`/piece/${createdPiece.id}`} className="report-row">
          <span className="report-row-icon">
            <Icon name="doc" size={18} />
          </span>
          <span className="report-row-body">
            <span className="report-row-title">{createdPiece.title}</span>
          </span>
        </Link>
        <div style={{ marginTop: 8 }}>
          <button className="btn-dec btn-review" onClick={onDone}>
            Done
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-form" style={{ marginTop: 8, paddingTop: 8, borderTop: "1px solid var(--rule)" }}>
      <p className="panel-empty" style={{ margin: "0 0 8px", textAlign: "left" }}>
        Creates a real, independent piece — drafted, fact-checked, and on the Kanban board like any
        other piece — using this piece's content as source material.
      </p>
      {isLoading && <div className="panel-empty">Loading eligible publications…</div>}
      {!isLoading && eligiblePublications.length === 0 && (
        <div className="panel-empty">
          No publication has an enabled LinkedIn Newsletter publish target configured yet.
        </div>
      )}
      {eligiblePublications.length > 0 && (
        <select
          className="settings-input"
          value={publicationId}
          onChange={(e) => setPublicationId(e.target.value ? Number(e.target.value) : "")}
        >
          <option value="">Select target publication…</option>
          {eligiblePublications.map((pub) => (
            <option key={pub.id} value={pub.id}>
              {pub.name}
            </option>
          ))}
        </select>
      )}
      <select
        className="settings-input"
        value={contentFormat}
        onChange={(e) => setContentFormat(e.target.value as "short" | "regular" | "long")}
        style={{ marginTop: 8 }}
      >
        <option value="short">Short</option>
        <option value="regular">Regular</option>
        <option value="long">Long</option>
      </select>
      <textarea
        className="settings-input"
        placeholder="Optional scope/twist — tell the model what angle to take"
        value={scope}
        onChange={(e) => setScope(e.target.value)}
        rows={2}
        style={{ marginTop: 8, width: "100%", resize: "vertical" }}
      />
      <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
        <button
          className="btn-dec btn-review"
          disabled={create.isPending || !publicationId}
          onClick={() => create.mutate()}
        >
          {create.isPending ? "Creating…" : "Create"}
        </button>
      </div>
      {create.isError && (
        <span className="settings-field-error">
          {create.error instanceof Error ? create.error.message : "Failed to create offspring."}
        </span>
      )}
    </div>
  );
}
